/**
 * 霓虹蛇境 (Neon Snake Realm) - 碰撞检测工具模块
 *
 * 本模块提供一组纯函数，用于处理游戏中所有的碰撞检测逻辑。
 * 所有函数均为无副作用的纯函数，输入参数明确，返回值确定。
 * 函数不依赖任何外部状态，仅基于传入的参数进行计算。
 *
 * 碰撞检测涵盖以下场景：
 * - 位置相等性判断
 * - 蛇身重叠检测
 * - 方向反向检测
 * - 墙壁边界碰撞
 * - 自身碰撞
 * - 食物收集检测
 * - 道具收集检测
 */

import type { Direction, Food, GridConfig, Obstacle, Position, PowerUpItem } from '@/types/game';

// ============================================================================
// 基础几何判断
// ============================================================================

/**
 * 判断两个网格位置是否相同。
 *
 * 通过比较 x 和 y 坐标的严格相等性来确定两个位置是否占据同一网格单元。
 * 此函数是其他所有碰撞检测函数的基础。
 *
 * @param a - 第一个位置
 * @param b - 第二个位置
 * @returns 如果两个位置的 x 和 y 坐标均相等则返回 true
 *
 * @example
 * positionsEqual({ x: 3, y: 5 }, { x: 3, y: 5 }) // => true
 * positionsEqual({ x: 3, y: 5 }, { x: 4, y: 5 }) // => false
 */
export function positionsEqual(a: Position, b: Position): boolean {
  return a.x === b.x && a.y === b.y;
}

// ============================================================================
// 蛇身重叠检测
// ============================================================================

/**
 * 检查指定位置是否与蛇身的任意节段重叠。
 *
 * 遍历蛇身数组的每个节段，使用 positionsEqual 进行逐一比对。
 * 时间复杂度为 O(n)，其中 n 为蛇身长度。
 *
 * @param position - 待检测的位置
 * @param snakeBody - 蛇身坐标数组（索引 0 为头部）
 * @returns 如果位置与蛇身任意节段重叠则返回 true
 *
 * @example
 * const body = [{ x: 5, y: 5 }, { x: 4, y: 5 }, { x: 3, y: 5 }];
 * isPositionOnSnake({ x: 4, y: 5 }, body) // => true
 * isPositionOnSnake({ x: 0, y: 0 }, body) // => false
 */
export function isPositionOnSnake(position: Position, snakeBody: Position[]): boolean {
  // 使用 some 方法进行短路求值，一旦找到匹配即停止遍历
  return snakeBody.some((segment) => positionsEqual(segment, position));
}

// ============================================================================
// 方向判断
// ============================================================================

/**
 * 判断两个移动方向是否互为反向。
 *
 * 反向的定义为：两个方向向量的点积为 -1。
 * 在网格移动中，这意味着两个方向在 x 轴和 y 轴上的分量互为相反数。
 *
 * 例如：
 * - 向右 (1, 0) 和 向左 (-1, 0) 互为反向
 * - 向上 (0, -1) 和 向下 (0, 1) 互为反向
 * - 向右 (1, 0) 和 向上 (0, -1) 不互为反向
 *
 * @param a - 第一个方向向量
 * @param b - 第二个方向向量
 * @returns 如果两个方向互为反向则返回 true
 *
 * @example
 * isOppositeDirection({ x: 1, y: 0 }, { x: -1, y: 0 }) // => true
 * isOppositeDirection({ x: 0, y: -1 }, { x: 0, y: 1 }) // => true
 * isOppositeDirection({ x: 1, y: 0 }, { x: 0, y: -1 }) // => false
 */
export function isOppositeDirection(a: Direction, b: Direction): boolean {
  // 两个方向反向当且仅当 x 分量之和为 0 且 y 分量之和为 0
  // 这等价于点积为 -1（在单位方向向量的前提下）
  return a.x + b.x === 0 && a.y + b.y === 0;
}

// ============================================================================
// 墙壁碰撞检测
// ============================================================================

/**
 * 检查蛇头是否超出游戏网格边界（撞墙）。
 *
 * 边界判定规则：
 * - x 坐标小于 0（左边界外）
 * - x 坐标大于等于 grid.width（右边界外）
 * - y 坐标小于 0（上边界外）
 * - y 坐标大于等于 grid.height（下边界外）
 *
 * 注意：此函数不处理穿墙护盾效果，穿墙逻辑应在调用方根据激活效果决定是否调用此函数。
 *
 * @param head - 蛇头当前所在的网格位置
 * @param grid - 游戏网格配置（包含宽度和高度）
 * @returns 如果蛇头超出边界则返回 true
 *
 * @example
 * const grid = { width: 20, height: 20, cellSize: 30 };
 * checkWallCollision({ x: -1, y: 10 }, grid) // => true（左边界外）
 * checkWallCollision({ x: 20, y: 10 }, grid) // => true（右边界外）
 * checkWallCollision({ x: 10, y: 10 }, grid) // => false（边界内）
 */
export function checkWallCollision(head: Position, grid: GridConfig): boolean {
  return head.x < 0 || head.x >= grid.width || head.y < 0 || head.y >= grid.height;
}

// ============================================================================
// 自身碰撞检测
// ============================================================================

/**
 * 检查蛇头是否与自身身体发生碰撞。
 *
 * 自身碰撞的定义：蛇头位置与身体任意节段（不包括头部自身）重叠。
 * 调用方传入的 body 参数应包含完整的蛇身（含头部），
 * 本函数会跳过索引 0（头部自身），从索引 1 开始检测。
 *
 * 性能优化：使用 slice(1) 创建不含头部的子数组，避免在循环中进行索引判断。
 * 对于较长的蛇身，可以考虑使用 Set 数据结构优化查找（O(1) vs O(n)），
 * 但考虑到蛇身长度通常有限（受网格大小限制），当前实现已足够高效。
 *
 * @param head - 蛇头当前所在的网格位置
 * @param body - 完整的蛇身坐标数组（索引 0 为头部）
 * @returns 如果蛇头与身体任意节段重叠则返回 true
 *
 * @example
 * const body = [{ x: 5, y: 5 }, { x: 4, y: 5 }, { x: 5, y: 5 }]; // 头部与第三节重叠
 * checkSelfCollision(body[0], body) // => true
 */
export function checkSelfCollision(head: Position, body: Position[]): boolean {
  // 从索引 1 开始检测，跳过头部自身
  const bodyWithoutHead = body.slice(1);
  return isPositionOnSnake(head, bodyWithoutHead);
}

// ============================================================================
// 食物碰撞检测
// ============================================================================

/**
 * 检查蛇头是否与食物位置重叠（吃到食物）。
 *
 * 此函数仅进行位置比较，不处理得分、蛇身增长等副作用。
 * 副作用应由调用方在检测到碰撞后处理。
 *
 * @param head - 蛇头当前所在的网格位置
 * @param food - 当前场景中的食物实体
 * @returns 如果蛇头与食物位置重叠则返回 true
 *
 * @example
 * const food = { position: { x: 7, y: 3 }, type: 'normal' };
 * checkFoodCollision({ x: 7, y: 3 }, food) // => true
 * checkFoodCollision({ x: 6, y: 3 }, food) // => false
 */
export function checkFoodCollision(head: Position, food: Food): boolean {
  return positionsEqual(head, food.position);
}

// ============================================================================
// 道具碰撞检测
// ============================================================================

/**
 * 检查蛇头是否与场景中的道具位置重叠（拾取道具）。
 *
 * 如果当前场景中没有道具（powerUpItem 为 null），则直接返回 false，
 * 无需进行位置比较。这避免了不必要的空值检查开销。
 *
 * 此函数仅进行位置比较，不处理道具效果激活、粒子生成等副作用。
 * 副作用应由调用方在检测到碰撞后处理。
 *
 * @param head - 蛇头当前所在的网格位置
 * @param powerUpItem - 当前场景中的道具实体（可为 null）
 * @returns 如果蛇头与道具位置重叠则返回 true；如果道具不存在则返回 false
 *
 * @example
 * const powerUp = { position: { x: 10, y: 5 }, type: PowerUpType.Speed, spawnTime: Date.now() };
 * checkPowerUpCollision({ x: 10, y: 5 }, powerUp) // => true
 * checkPowerUpCollision({ x: 9, y: 5 }, powerUp) // => false
 * checkPowerUpCollision({ x: 10, y: 5 }, null)   // => false
 */
export function checkPowerUpCollision(head: Position, powerUpItem: PowerUpItem | null): boolean {
  // 短路求值：如果道具不存在，直接返回 false
  if (powerUpItem === null) {
    return false;
  }
  return positionsEqual(head, powerUpItem.position);
}

// ============================================================================
// 障碍物碰撞检测
// ============================================================================

/**
 * 检查蛇头是否与场景中的障碍物碰撞。
 *
 * 遍历所有障碍物，检查蛇头位置是否与任一障碍物重叠。
 * 使用 some 方法进行短路求值，一旦找到匹配即停止遍历。
 *
 * @param head - 蛇头当前所在的网格位置
 * @param obstacles - 场景中的障碍物列表
 * @returns 如果蛇头与任一障碍物重叠则返回 true
 */
export function checkObstacleCollision(head: Position, obstacles: Obstacle[]): boolean {
  return obstacles.some((obstacle) => positionsEqual(head, obstacle.position));
}